import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { glassCard, beepAcesso } from "./MobileFamiliares";
import { ArrowLeft, CheckCircle2, Loader2, Mic, XCircle } from "lucide-react";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";
const MAX_GRAV_MS = 4000;
const SILENCIO_MS = 650;
const LIMIAR_FALA = 0.02;
const CHAVE_PILOTO = "voz_piloto_auto";
const CHAVE_ULTIMO = "voz_ultimo_comando";
const CHAVE_DICA = "voz_dica_count";
const DICA_MAX_USOS = 3;

const MOTIVOS_ACESSO = {
  ponto_invalido: "Ponto de acesso inválido ou inativo. Avise a administração.",
  fabricante_nao_suportado: "Este ponto ainda não é acionável pelo app (fase atual: somente ESP32).",
  usuario_nao_encontrado: "Cadastro não encontrado. Faça login novamente.",
  cadastro_nao_encontrado: "Cadastro não encontrado. Faça login novamente.",
  inativo: "Seu cadastro está inativo. Procure a administração.",
  bloqueado: "Acesso bloqueado. Procure a administração.",
  fora_validade: "Cadastro fora da validade. Procure a administração.",
  falta_autorizacao: "Sem autorização ativa em nenhuma unidade.",
  facial_requerido: "Validação facial expirada. Feche e valide o rosto novamente.",
};

const falar = (texto) => {
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(texto);
    u.lang = "pt-BR";
    window.speechSynthesis.speak(u);
  } catch { /* TTS indisponível no aparelho */ }
};

export default function MobileVoz({ origem, eql, cpf, facialToken, comandos, onClose }) {
  const [fase, setFase] = useState("iniciando"); // iniciando|escutando|gravando|processando|confirmar|resultado|erro
  const [transcricao, setTranscricao] = useState("");
  const [sugestao, setSugestao] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [reconhecido, setReconhecido] = useState(null); // {texto, transcricao} — destaque do botão casado
  const [erroMsg, setErroMsg] = useState("");
  const [pilotoAuto, setPilotoAuto] = useState(() => localStorage.getItem(CHAVE_PILOTO) === "1");
  // onboarding: conta cada abertura do Voice e mostra a dica só nas primeiras 3
  const [mostrarDica] = useState(() => {
    const n = (parseInt(localStorage.getItem(CHAVE_DICA) || "0", 10) || 0) + 1;
    try { localStorage.setItem(CHAVE_DICA, String(n)); } catch { /* storage indisponível */ }
    return n <= DICA_MAX_USOS;
  });

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const chunksRef = useRef([]);
  const falouRef = useRef(false);
  const ultimoSomRef = useRef(0);
  const enviandoRef = useRef(false);

  const ultimo = localStorage.getItem(CHAVE_ULTIMO);
  const lista = pilotoAuto && ultimo && comandos.some((c) => c.texto === ultimo)
    ? [comandos.find((c) => c.texto === ultimo), ...comandos.filter((c) => c.texto !== ultimo)]
    : comandos;
  const paginas = [];
  for (let i = 0; i < lista.length; i += 5) paginas.push(lista.slice(i, i + 5));

  const pararTudo = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch { /* já parado */ }
    }
    recRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
  }, []);

  const disparar = useCallback(async (cmd) => {
    // toque em um comando cancela qualquer captura de voz em andamento
    enviandoRef.current = true;
    pararTudo();
    setSugestao(null);
    setFase("processando");
    // sem fala antes do disparo: a voz diz só o resultado (evita vozes encavaladas)
    try {
      const r = await fetch(`${API}/public/acesso-qr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origem, eql, cpf, ponto: cmd.ponto,
          empreendimento_codigo: cmd.emp || undefined,
          facial_token: facialToken,
          device_info: { os: /android/i.test(navigator.userAgent) ? "android" : "web", via: "voz" },
          timestamp: new Date().toISOString(),
        }),
      });
      const d = await r.json();
      if (d.ok && (d.resultado === "acesso_liberado" || d.resultado === "acesso_negado")) {
        if (d.resultado === "acesso_liberado") localStorage.setItem(CHAVE_ULTIMO, cmd.texto);
        setResultado(d);
      } else {
        setResultado({ resultado: "acesso_negado", motivo: d.motivo || "falha" });
      }
    } catch {
      setResultado({ resultado: "acesso_negado", motivo: "falha" });
    }
    setFase("resultado");
  }, [origem, eql, cpf, facialToken, pararTudo]);

  const enviarAudio = useCallback(async () => {
    const blob = new Blob(chunksRef.current, { type: recRef.current?.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 800) { setFase("escutando"); return; }
    setFase("processando");
    try {
      const b64 = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(",")[1] || "");
        fr.onerror = rej;
        fr.readAsDataURL(blob);
      });
      const formato = blob.type.includes("mp4") ? "mp4" : "webm";
      const r = await fetch(`${API}/public/voz/comando`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf, facial_token: facialToken, audio_b64: b64, formato }),
      });
      const d = await r.json();
      if (d.ok && d.acao === "disparar") {
        setReconhecido({ texto: d.comando.texto, transcricao: d.transcricao || "" });
        disparar(d.comando);
        return;
      }
      if (d.ok && d.acao === "confirmar") {
        setTranscricao(d.transcricao || "");
        setSugestao(d.comando);
        setFase("confirmar");
        return;
      }
      if (d.motivo === "facial_expirado") {
        setErroMsg("A validação facial expirou. Feche e toque em Voice novamente.");
        setFase("erro");
        return;
      }
      if (d.motivo === "muitas_tentativas") {
        setErroMsg("Muitas tentativas de voz. Aguarde 1 minuto e tente de novo.");
        setFase("erro");
        return;
      }
      toast.error(d.motivo === "incompreensivel"
        ? "Não ouvi direito. Fale novamente ou toque no comando."
        : "Comando não reconhecido. Fale novamente ou toque no comando.");
      setFase("escutando");
      // reinicia a escuta para nova tentativa dentro da validade do token
      iniciarRef.current?.();
    } catch {
      toast.error("Falha de conexão. Toque no comando ou tente de novo.");
      setFase("escutando");
      iniciarRef.current?.();
    }
  }, [origem, eql, cpf, facialToken, disparar]);

  const iniciarEscuta = useCallback(async () => {
    pararTudo();
    enviandoRef.current = false;
    falouRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = "";
      for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => { if (!enviandoRef.current) { enviandoRef.current = true; enviarAudio(); } };
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const inicio = Date.now();
      rec.start(250);
      setFase("escutando");
      intervalRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let soma = 0;
        for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; soma += v * v; }
        const rms = Math.sqrt(soma / buf.length);
        const agora = Date.now();
        if (rms > LIMIAR_FALA) {
          if (!falouRef.current) { falouRef.current = true; setFase("gravando"); }
          ultimoSomRef.current = agora;
        }
        const calou = falouRef.current && agora - ultimoSomRef.current > SILENCIO_MS;
        if (calou || agora - inicio > MAX_GRAV_MS) {
          if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
          if (rec.state !== "inactive") rec.stop();
        }
      }, 100);
      timeoutRef.current = setTimeout(() => {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        if (rec.state !== "inactive") rec.stop();
      }, MAX_GRAV_MS + 400);
    } catch (e) {
      const nome = e?.name || "";
      setErroMsg(
        nome === "NotAllowedError"
          ? "Microfone bloqueado pelo aplicativo (WebView não delegou a permissão). No navegador funciona; o APK precisa conceder RESOURCE_AUDIO_CAPTURE no WebChromeClient.onPermissionRequest."
          : nome === "NotFoundError"
            ? "Nenhum microfone encontrado neste aparelho."
            : nome === "NotReadableError"
              ? "Microfone em uso por outro aplicativo. Feche-o e tente de novo."
              : "Não foi possível acessar o microfone. Toque no comando desejado abaixo.");
      setFase("erro");
    }
  }, [pararTudo, enviarAudio]);

  const iniciarRef = useRef(iniciarEscuta);
  iniciarRef.current = iniciarEscuta;

  useEffect(() => {
    iniciarEscuta();
    return pararTudo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // resultado (liberado/negado): beep + fala o status, e fecha sozinho após 5s voltando ao dashboard
  useEffect(() => {
    if (fase !== "resultado") return undefined;
    const ok = resultado?.resultado === "acesso_liberado";
    beepAcesso(ok);
    falar(ok ? "Acesso liberado" : "Acesso não liberado");
    const t = setTimeout(() => { pararTudo(); onClose(); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const togglePiloto = (v) => {
    setPilotoAuto(v);
    localStorage.setItem(CHAVE_PILOTO, v ? "1" : "0");
  };

  const piscando = fase === "escutando";
  const liberado = resultado?.resultado === "acesso_liberado";

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-voz-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-voz-voltar" aria-label="Voltar"
          onClick={() => { pararTudo(); onClose(); }}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }}
          data-testid="appmobile-voz-titulo">Acesso por Voz</h1>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-10 flex flex-col items-center">
        {fase !== "resultado" && (
          <>
            <p className={`text-sm text-center ${mostrarDica ? "mb-2" : "mb-4"}`} style={{ color: "#e1e2ed" }}
              data-testid="appmobile-voz-guia">Fale o número ou o nome do acesso</p>

            {mostrarDica && (
              <p className="text-[11px] text-center mb-4 px-3 py-1.5 rounded-full"
                style={{ background: "rgba(250,204,21,0.12)", color: "#fde68a", border: "1px solid rgba(250,204,21,0.25)" }}
                data-testid="appmobile-voz-dica-numero">
                Dica: fale só o número
              </p>
            )}

            <div className="relative w-24 h-24 rounded-full flex items-center justify-center mb-2"
              style={glassCard} data-testid="appmobile-voz-mic">
              {piscando && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-40"
                  style={{ background: "#2563EB" }} />
              )}
              {fase === "processando" || fase === "iniciando"
                ? <Loader2 className="h-9 w-9 animate-spin" style={{ color: "#8ab4ff" }} />
                : <Mic className="h-9 w-9" style={{ color: fase === "gravando" ? "#7ef0a0" : "#8ab4ff" }} />}
            </div>
            <p className="text-xs mb-5" style={{ color: "#8d90a0" }} data-testid="appmobile-voz-status">
              {fase === "iniciando" && "Abrindo microfone..."}
              {fase === "escutando" && "Pode falar"}
              {fase === "gravando" && "Ouvindo..."}
              {fase === "processando" && "Reconhecendo comando..."}
              {fase === "confirmar" && "Confirme o comando"}
              {fase === "erro" && "Microfone indisponível"}
            </p>

            {reconhecido && (
              <div className="w-full max-w-sm rounded-[14px] px-4 py-3 mb-4 relative overflow-hidden" style={glassCard}
                data-testid="appmobile-voz-reconhecido">
                <p className="text-[11px]" style={{ color: "#8d90a0" }}>Você disse: "{reconhecido.transcricao}"</p>
                <p className="text-sm font-semibold" style={{ color: "#7ef0a0" }}>
                  Entendi: {reconhecido.texto}
                </p>
              </div>
            )}

            {fase === "confirmar" && sugestao && (
              <div className="w-full max-w-sm rounded-[14px] p-4 mb-4 relative overflow-hidden" style={glassCard}
                data-testid="appmobile-voz-confirmar">                <p className="text-xs mb-1" style={{ color: "#8d90a0" }}>Você disse: "{transcricao}"</p>
                <p className="text-sm font-semibold mb-3" style={{ color: "#e1e2ed" }}>
                  Entendi: <span style={{ color: "#8ab4ff" }}>{sugestao.texto}</span>
                </p>
                <div className="flex gap-2">
                  <button type="button" data-testid="appmobile-voz-confirmar-sim"
                    onClick={() => disparar(sugestao)}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-transform"
                    style={{ background: "#2563EB", touchAction: "manipulation" }}>
                    Confirmar
                  </button>
                  <button type="button" data-testid="appmobile-voz-confirmar-nao"
                    onClick={() => { setSugestao(null); iniciarEscuta(); }}
                    className="flex-1 h-11 rounded-xl text-sm font-semibold active:scale-[0.98] transition-transform"
                    style={{ background: "rgba(255,255,255,0.08)", color: "#c3c6d7", touchAction: "manipulation" }}>
                    Tentar de novo
                  </button>
                </div>
              </div>
            )}

            {fase === "erro" && (
              <p className="text-xs text-center mb-4 px-4" style={{ color: "#ffb4ab" }}
                data-testid="appmobile-voz-erro">{erroMsg}</p>
            )}

            <div className="w-full max-w-sm overflow-x-auto snap-x snap-mandatory flex"
              style={{ scrollbarWidth: "none" }} data-testid="appmobile-voz-carrossel">
              {paginas.map((pag, pi) => (
                <div key={pi} className="min-w-full snap-start flex flex-col gap-2 px-0.5"
                  data-testid={`appmobile-voz-pagina-${pi}`}>
                  {pag.map((c) => {
                    const num = comandos.indexOf(c) + 1;
                    const casou = reconhecido?.texto === c.texto;
                    return (
                      <button key={c.texto} type="button" data-testid={`appmobile-voz-cmd-${num}`}
                        onClick={() => disparar(c)}
                        disabled={fase === "processando"}
                        className={`w-full rounded-xl px-4 py-3.5 flex items-center gap-3 text-left active:scale-[0.98] transition-transform disabled:opacity-40 relative overflow-hidden ${casou ? "animate-pulse" : ""}`}
                        style={casou
                          ? { background: "rgba(126,240,160,0.2)", border: "1px solid rgba(126,240,160,0.6)", touchAction: "manipulation" }
                          : { ...glassCard, touchAction: "manipulation" }}>
                        <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                          style={{ background: "rgba(37,99,235,0.25)", color: "#8ab4ff" }}>{num}</span>
                        <span className="text-sm font-medium" style={{ color: "#e1e2ed" }}>{c.texto}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            {paginas.length > 1 && (
              <p className="text-[11px] mt-2" style={{ color: "#8d90a0" }} data-testid="appmobile-voz-dicas">
                Deslize para o lado para ver mais comandos
              </p>
            )}

            <label className="mt-5 flex items-center gap-2 cursor-pointer" data-testid="appmobile-voz-piloto-label">
              <input type="checkbox" checked={pilotoAuto} data-testid="appmobile-voz-piloto-auto"
                onChange={(e) => togglePiloto(e.target.checked)} className="w-4 h-4 accent-blue-600" />
              <span className="text-[11px]" style={{ color: "#8d90a0" }}>Manter último comando como primeiro</span>
            </label>
          </>
        )}

        {fase === "resultado" && resultado && (
          <div className="w-full max-w-sm rounded-[14px] p-6 flex flex-col items-center relative overflow-hidden"
            style={glassCard} data-testid="appmobile-voz-resultado">
            {liberado
              ? <CheckCircle2 className="h-14 w-14 mb-3" style={{ color: "#7ef0a0" }} />
              : <XCircle className="h-14 w-14 mb-3" style={{ color: "#ffb4ab" }} />}
            <p className="text-base font-bold mb-1" style={{ color: liberado ? "#7ef0a0" : "#ffb4ab" }}
              data-testid="appmobile-voz-resultado-status">
              {liberado ? "Acesso liberado" : "Acesso não liberado"}
            </p>
            <p className="text-xs text-center" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-voz-resultado-detalhe">
              {liberado
                ? (resultado.dispositivo || resultado.local || "Dispositivo acionado com sucesso.")
                : (MOTIVOS_ACESSO[resultado.motivo] || "Não foi possível acionar agora. Tente novamente.")}
            </p>
            <div className="mt-4 h-1 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}
              data-testid="appmobile-voz-resultado-autoclose" aria-label="Fechando automaticamente em 5 segundos">
              <div className="mobile-countdown-bar h-full rounded-full"
                style={{ background: liberado ? "#7ef0a0" : "#ffb4ab" }} />
            </div>
            <button type="button" data-testid="appmobile-voz-resultado-fechar"
              onClick={() => { pararTudo(); onClose(); }}
              className="w-full h-11 rounded-xl text-sm font-semibold text-white mt-5 active:scale-[0.98] transition-transform"
              style={{ background: "#2563EB", touchAction: "manipulation" }}>
              Concluir
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
